import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { NavLink as RouterNavLink } from 'react-router-dom';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  Navbar,
  NavItem,
  NavLink,
  UncontrolledDropdown,
} from 'reactstrap';
import classNames from 'classnames';

import headerMenu from '~constants/headerMenu';

import CompanyLogo from '~components/CompanyLogo';
import { AnnouncementMenu } from '~main/components';
import DocumentBatchesMenu from '~main/components/document-batch-jobs/DocumentBatchesMenu';
import UserMenu from '~main/components/UserMenu';

import { getFeatureFlags } from '~main/actions/login';

import {
  selectAuthenticatedUser,
  selectCompanyDefaults,
  selectCompanySettings,
  selectUserTags,
} from '~main/selectors/login';

/**
 * Header component renders the main navigation bar, including company logo, menu items, and user menu.
 *
 * fetches user authentication details, company settings, and user tags and renders
 * navigation menu based on user authorization and company settings.
 *
 * @component
 * @returns The rendered Header component.
 */
const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const user = useAppSelector(selectAuthenticatedUser);
  const companyDefaults = useAppSelector(selectCompanyDefaults);
  const tags = useAppSelector(selectUserTags);
  const settings = useAppSelector(selectCompanySettings);

  const [featureFlagStatus, setFeatureFlagStatus] = useState<Record<string, boolean>>({});

  // Check feature flags for menu items
  useEffect(() => {
    const fetchFeatureFlags = async () => {
      const menus = headerMenu(user, settings, companyDefaults);
      // Collect all feature flags from menu items and their children
      const featureFlags = new Set<string>();

      menus.forEach((item) => {
        if (item.featureFlag) featureFlags.add(item.featureFlag);
        item.children?.forEach((child) => {
          if (child.featureFlag) featureFlags.add(child.featureFlag);
        });
      });

      const flagsArray = Array.from(featureFlags);
      if (flagsArray.length > 0) {
        const { payload } = await dispatch(getFeatureFlags({ flags: flagsArray }));
        if (payload?.success) {
          setFeatureFlagStatus(payload.data);
        }
      }
    };

    fetchFeatureFlags();
  }, [user, settings, companyDefaults]);

  const isFeatureEnabled = (flag?: string) => {
    // Automatically return true if no flag provided.
    if (!flag) return true;
    return !!featureFlagStatus[flag];
  };

  return (
    <Navbar className="menu">
      <div className="d-flex flex-nowrap justify-content-between align-items-center w-100 ">
        <div className="d-flex align-items-center">
          <CompanyLogo logo={user?.company.logo} altText={user?.company.name ?? ''} />
          <Nav>
            {headerMenu(user, settings, companyDefaults).map(
              ({ caption, link = '', children = [], authTag = null, featureFlag }, index) => {
                const isAuthorized = (!authTag || tags.includes(authTag)) && isFeatureEnabled(featureFlag);
                if (!isAuthorized) return null;

                const headerClass = classNames('menu-link', {
                  [`active`]: openMenuIndex === index,
                });

                // avoid rendering empty headers if user has no authTags or feature flag is disabled
                const authorizedChildren = children.filter(
                  ({ authTag, featureFlag }) => (!authTag || tags.includes(authTag)) && isFeatureEnabled(featureFlag),
                );
                if (!authorizedChildren.length && !link) return null;

                if (authorizedChildren.length > 0) {
                  return (
                    <UncontrolledDropdown
                      nav
                      inNavbar
                      onMouseEnter={() => setOpenMenuIndex(index)}
                      onMouseLeave={() => setOpenMenuIndex(null)}
                      toggle={() => setOpenMenuIndex(openMenuIndex !== index ? index : null)}
                      isOpen={openMenuIndex === index}
                      key={index}
                    >
                      <DropdownToggle nav caret className={headerClass}>
                        {caption}
                      </DropdownToggle>
                      <DropdownMenu>
                        {authorizedChildren.map(({ caption, link }, childIndex) => (
                          <DropdownItem key={childIndex} tag={RouterNavLink} to={link} className="menu-dropdown-item">
                            {caption}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  );
                }

                return (
                  <NavItem key={index}>
                    <NavLink href={link} className="menu-link">
                      {caption}
                    </NavLink>
                  </NavItem>
                );
              },
            )}
          </Nav>
        </div>
        <div className="d-flex align-items-center ps-4">
          <DocumentBatchesMenu />
          <AnnouncementMenu />
          <UserMenu />
        </div>
      </div>
    </Navbar>
  );
};

export default Header;
